import { 
  LayoutDashboard, 
  CreditCard, 
  Briefcase, 
  Users, 
  Settings,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Bell,
  Building2,
  TrendingUp,
  Clock,
  RefreshCw,
  Receipt,
  UserCircle,
  PieChart,
  FileText,
  Sparkles,
  CalendarDays,
  HandCoins,
  Landmark,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  description?: string;
  subItems?: { id: string; label: string; icon?: React.ElementType; description?: string }[];
}

export type PaymentsSubView = "collections" | "recurring" | "history";
export type ServicesSubView = "services" | "clients";
export type ReportsSubView = "analytics" | "activity" | "customers";
export type ProfileSubView = "info" | "bank" | "team";
export type SettingsSubView = "billing" | "preferences" | "notifications";

export interface ProfessionalTerminology {
  orders?: string;
  products?: string;
  customers?: string;
  store?: string;
}

interface ProfessionalSidebarProps {
  activeTab: string;
  paymentsSubView: PaymentsSubView;
  servicesSubView: ServicesSubView;
  reportsSubView: ReportsSubView;
  profileSubView: ProfileSubView;
  settingsSubView: SettingsSubView;
  onTabChange: (tab: string, subView?: string) => void;
  onPaymentsSubViewChange: (view: PaymentsSubView) => void;
  onServicesSubViewChange: (view: ServicesSubView) => void;
  onReportsSubViewChange: (view: ReportsSubView) => void;
  onProfileSubViewChange: (view: ProfileSubView) => void;
  onSettingsSubViewChange: (view: SettingsSubView) => void;
  pendingPaymentsCount: number;
  pendingFeesCount: number;
  canAccessSettings: boolean;
  isSuperAdmin: boolean;
  terminology?: ProfessionalTerminology;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ProfessionalSidebar({
  activeTab,
  paymentsSubView,
  servicesSubView,
  reportsSubView,
  profileSubView,
  settingsSubView,
  onTabChange,
  onPaymentsSubViewChange,
  onServicesSubViewChange,
  onReportsSubViewChange,
  onProfileSubViewChange,
  onSettingsSubViewChange,
  pendingPaymentsCount,
  pendingFeesCount,
  canAccessSettings,
  isSuperAdmin,
  terminology = {},
}: ProfessionalSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  // Apply terminology preferences with defaults
  const labels = {
    payments: terminology.orders || "Payments",
    services: terminology.products || "Services",
    clients: terminology.customers || "Clients",
    business: terminology.store || "Business",
  };

  const navItems: NavItem[] = [
    {
      id: "overview",
      label: "Dashboard",
      icon: LayoutDashboard,
      description: "Your business at a glance",
    },
    {
      id: "services",
      label: "Manage",
      icon: Briefcase,
      description: "Services and clients",
      subItems: [
        { id: "services", label: labels.services, icon: CalendarDays, description: "Your offerings" },
        { id: "clients", label: labels.clients, icon: UserCircle, description: "Client directory" },
      ],
    },
    {
      id: "payments",
      label: "Collect",
      icon: HandCoins,
      badge: pendingPaymentsCount > 0 ? pendingPaymentsCount : undefined,
      description: "Manage payment collections",
      subItems: [
        { id: "collections", label: "Active", icon: Receipt, description: "Ongoing collections" },
        { id: "recurring", label: "Recurring", icon: RefreshCw, description: "Auto-recurring schedules" },
        { id: "history", label: "Completed", icon: Clock, description: "Past transactions" },
      ],
    },
    {
      id: "reports",
      label: "Insights",
      icon: PieChart,
      description: "Analytics and reports",
      subItems: [
        { id: "analytics", label: "Performance", icon: TrendingUp, description: "Key metrics" },
        { id: "activity", label: "Activity Log", icon: FileText, description: "Recent actions" },
        { id: "customers", label: "Client Trends", icon: Users, description: "Customer analytics" },
      ],
    },
    {
      id: "profile",
      label: "Profile",
      icon: Building2,
      description: "Business profile & team",
      subItems: [
        { id: "info", label: "Business Info", icon: Building2, description: "Business details" },
        { id: "bank", label: "Your Bank", icon: Landmark, description: "Bank account details" },
        ...(isSuperAdmin ? [{ id: "team", label: "Team", icon: UserPlus, description: "Manage team members" }] : []),
      ],
    },
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      badge: pendingFeesCount > 0 ? pendingFeesCount : undefined,
      description: "Account settings",
      subItems: canAccessSettings ? [
        { id: "billing", label: "Subscription", icon: Wallet, description: "Plan & billing" },
        { id: "preferences", label: "Preferences", icon: Sparkles, description: "Customize experience" },
        { id: "notifications", label: "Alerts", icon: Bell, description: "Notification settings" },
      ] : [
        { id: "notifications", label: "Alerts", icon: Bell, description: "Notification settings" },
      ],
    },
  ];

  const handleItemClick = (item: NavItem, subItemId?: string) => {
    if (subItemId) {
      onTabChange(item.id, subItemId);
    } else {
      if (item.subItems && item.subItems.length > 0) {
        const currentSubView = getActiveSubView(item.id) || item.subItems[0].id;
        onTabChange(item.id, currentSubView);
      } else {
        onTabChange(item.id);
      }
    }
  };

  const getActiveSubView = (itemId: string) => {
    switch (itemId) {
      case "payments": return paymentsSubView;
      case "services": return servicesSubView;
      case "reports": return reportsSubView;
      case "profile": return profileSubView;
      case "settings": return settingsSubView;
      default: return "";
    }
  };

  return (
    <aside 
      className={cn(
        "bg-gradient-to-b from-card to-card/95 border-r h-full flex flex-col transition-all duration-300 ease-in-out",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-cyan-500/5 to-teal-500/5">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Workspace</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.icon;
          const hasSubItems = item.subItems && item.subItems.length > 0;

          return (
            <div key={item.id} className="relative">
              {/* Main Nav Item */}
              <button
                onClick={() => handleItemClick(item, hasSubItems ? item.subItems![0].id : undefined)}
                data-tour={`${item.id}-tab`}
                title={collapsed ? item.label : undefined}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-gradient-to-r from-cyan-500/15 to-teal-500/10 text-cyan-700 dark:text-cyan-400 shadow-sm border border-cyan-500/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                )}
              >
                <Icon className={cn(
                  "h-5 w-5 flex-shrink-0 transition-colors",
                  isActive ? "text-cyan-600 dark:text-cyan-400" : ""
                )} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{item.label}</span>
                    {item.badge && (
                      <Badge 
                        className={cn(
                          "h-5 min-w-5 px-1.5 text-xs font-semibold",
                          isActive 
                            ? "bg-cyan-500 text-white" 
                            : "bg-cyan-500/20 text-cyan-700 dark:text-cyan-400"
                        )}
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
                {collapsed && item.badge && (
                  <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-cyan-500 animate-pulse" />
                )}
              </button>

              {/* Sub Items */}
              {hasSubItems && isActive && !collapsed && (
                <div className="mt-1 ml-3 space-y-0.5 border-l-2 border-cyan-500/20 pl-3">
                  {item.subItems!.map((subItem) => {
                    const isSubActive = getActiveSubView(item.id) === subItem.id;
                    const SubIcon = subItem.icon;

                    return (
                      <button
                        key={subItem.id}
                        onClick={() => handleItemClick(item, subItem.id)}
                        className={cn(
                          "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all duration-150",
                          isSubActive
                            ? "bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                        )}
                      >
                        {SubIcon && (
                          <SubIcon className={cn(
                            "h-4 w-4 flex-shrink-0",
                            isSubActive ? "text-cyan-600 dark:text-cyan-400" : ""
                          )} />
                        )}
                        <span className="truncate">{subItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
